import { api } from '@/services/api';
import {
    Connector,
    ConnectorCreate,
    ConnectorStatus,
    ConnectorType,
    ConnectorUpdate,
    PipelineType,
} from '@/types';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

interface ConnectorFilters {
  type?: ConnectorType;
  status?: ConnectorStatus;
  pipeline?: PipelineType;
  search?: string;
}

interface ConnectorSort {
  field: keyof Connector;
  direction: 'asc' | 'desc';
}

interface ConnectorPagination {
  page: number;
  pageSize: number;
}

interface ConnectorState {
  // State
  connectors: Connector[];
  selectedConnector: Connector | null;
  loading: boolean;
  error: Error | null;
  filters: ConnectorFilters;
  sort: ConnectorSort;
  pagination: ConnectorPagination;

  // Computed
  filteredConnectors: Connector[];
  sortedConnectors: Connector[];
  paginatedConnectors: Connector[];
  totalPages: number;

  // Actions
  fetchConnectors: () => Promise<void>;
  createConnector: (data: ConnectorCreate) => Promise<Connector>;
  updateConnector: (id: string, data: ConnectorUpdate) => Promise<void>;
  deleteConnector: (id: string) => Promise<void>;
  clearSyncToken: (id: string) => Promise<void>;
  selectConnector: (id: string | null) => void;
  setConnectors: (connectors: Connector[]) => void;
  setFilter: (filters: ConnectorFilters) => void;
  setSort: (sort: ConnectorSort) => void;
  setPagination: (pagination: ConnectorPagination) => void;
  resetFilters: () => void;
}

const defaultSort: ConnectorSort = { field: 'name', direction: 'asc' };
const defaultPagination: ConnectorPagination = { page: 1, pageSize: 10 };

export const useConnectorStore = create<ConnectorState>()(
  immer((set, get) => ({
    // Initial state
    connectors: [],
    selectedConnector: null,
    loading: false,
    error: null,
    filters: {},
    sort: defaultSort,
    pagination: defaultPagination,

    // Computed getters
    get filteredConnectors() {
      const { connectors, filters } = get();
      if (!filters || Object.keys(filters).length === 0) return connectors;

      return connectors.filter(connector => {
        if (filters.type && connector.type !== filters.type) return false;
        if (filters.status && connector.status !== filters.status) return false;
        if (
          filters.pipeline &&
          connector.settings.pipeline !== filters.pipeline
        )
          return false;
        if (filters.search) {
          const search = filters.search.toLowerCase();
          return (
            connector.name.toLowerCase().includes(search) ||
            connector.resourceRef.toLowerCase().includes(search)
          );
        }
        return true;
      });
    },

    get sortedConnectors() {
      const { filteredConnectors, sort } = get();
      const sorted = [...filteredConnectors];

      sorted.sort((a, b) => {
        const aValue = a[sort.field];
        const bValue = b[sort.field];

        if (aValue === bValue) return 0;
        if (aValue === undefined || bValue === undefined) return 0;

        const comparison = aValue < bValue ? -1 : 1;
        return sort.direction === 'asc' ? comparison : -comparison;
      });

      return sorted;
    },

    get paginatedConnectors() {
      const { sortedConnectors, pagination } = get();
      const start = (pagination.page - 1) * pagination.pageSize;
      const end = start + pagination.pageSize;
      return sortedConnectors.slice(start, end);
    },

    get totalPages() {
      const { sortedConnectors, pagination } = get();
      return Math.ceil(sortedConnectors.length / pagination.pageSize);
    },

    // Actions
    fetchConnectors: async () => {
      set(state => {
        state.loading = true;
        state.error = null;
      });

      try {
        const connectors = await api.connectors.list();
        set(state => {
          state.connectors = connectors;
          state.loading = false;
        });
      } catch (error) {
        set(state => {
          state.error = error as Error;
          state.loading = false;
        });
        throw error;
      }
    },

    createConnector: async data => {
      // Optimistic update - add temporary connector
      const tempId = `temp-${Date.now()}`;
      const tempConnector: Connector = {
        id: tempId,
        ...data,
        status: 'active',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        documentsProcessed: 0,
      };

      set(state => {
        state.connectors.push(tempConnector);
      });

      try {
        const created = await api.connectors.create(data);

        // Replace temp connector with real one
        set(state => {
          const index = state.connectors.findIndex(c => c.id === tempId);
          if (index !== -1) {
            state.connectors[index] = created;
          }
        });

        return created;
      } catch (error) {
        // Rollback on error
        set(state => {
          state.connectors = state.connectors.filter(c => c.id !== tempId);
        });
        throw error;
      }
    },

    updateConnector: async (id, data) => {
      // Store original for rollback
      const original = get().connectors.find(c => c.id === id);
      if (!original) throw new Error('Connector not found');

      // Optimistic update
      set(state => {
        const connector = state.connectors.find(c => c.id === id);
        if (connector) {
          Object.assign(connector, data, {
            updatedAt: new Date().toISOString(),
          });
        }
      });

      try {
        const updated = await api.connectors.update(id, data);

        // Update with server response
        set(state => {
          const index = state.connectors.findIndex(c => c.id === id);
          if (index !== -1) {
            state.connectors[index] = updated;
          }
        });
      } catch (error) {
        // Rollback on error
        set(state => {
          const index = state.connectors.findIndex(c => c.id === id);
          if (index !== -1) {
            state.connectors[index] = original;
          }
        });
        throw error;
      }
    },

    deleteConnector: async id => {
      // Store original for rollback
      const connectors = [...get().connectors];

      // Optimistic delete
      set(state => {
        state.connectors = state.connectors.filter(c => c.id !== id);
        if (state.selectedConnector?.id === id) {
          state.selectedConnector = null;
        }
      });

      try {
        await api.connectors.delete(id);
      } catch (error) {
        // Rollback on error
        set(state => {
          state.connectors = connectors;
        });
        throw error;
      }
    },

    clearSyncToken: async id => {
      await api.connectors.clearSyncToken(id);
      // No state change needed - sync token is not part of the connector object
    },

    selectConnector: id => {
      set(state => {
        state.selectedConnector = id
          ? state.connectors.find(c => c.id === id) || null
          : null;
      });
    },

    setConnectors: connectors => {
      set(state => {
        state.connectors = connectors;
      });
    },

    setFilter: filters => {
      set(state => {
        state.filters = { ...state.filters, ...filters };
        state.pagination.page = 1; // Reset to first page when filtering
      });
    },

    setSort: sort => {
      set(state => {
        state.sort = sort;
      });
    },

    setPagination: pagination => {
      set(state => {
        state.pagination = pagination;
      });
    },

    resetFilters: () => {
      set(state => {
        state.filters = {};
        state.sort = defaultSort;
        state.pagination = defaultPagination;
      });
    },
  }))
);
